import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, RefreshCw } from 'lucide-react';
import apiService from '../../services/api';

const StatusManagement = () => {
  const { t } = useTranslation();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ value: '', code: '', labelTr: '', labelEn: '', color: '#9ca3af', sortOrder: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const defs = await apiService.request('/chat/status-definitions');
      setStatuses(defs || []);
    } catch (err) {
      console.error('Cannot load status definitions', err);
      setStatuses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setEditing(null);
    setForm({ value: '', code: '', labelTr: '', labelEn: '', color: '#9ca3af', sortOrder: statuses.length });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (!form.code || form.value === '' || !form.labelTr || !form.labelEn) {
        throw new Error(t('admin.statuses.fillAll'));
      }

      const payload = {
        value: Number(form.value),
        code: form.code,
        labelTr: form.labelTr,
        labelEn: form.labelEn,
        color: form.color,
        sortOrder: Number(form.sortOrder)
      };

      if (editing) {
        await apiService.request(`/chat/status-definitions/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        await apiService.request('/chat/status-definitions', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      await load();
      resetForm();

    } catch (err) {
      setError(err.message || t('admin.statuses.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (status) => {
    setEditing(status);
    setForm({
      value: status.value,
      code: status.code,
      labelTr: status.labelTr,
      labelEn: status.labelEn,
      color: status.color,
      sortOrder: status.sortOrder || 0
    });
    setError('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.statuses.confirmDelete'))) return;
    try {
      await apiService.request(`/chat/status-definitions/${id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      alert(t('admin.statuses.deleteFailed') + ': ' + (err.message || t('admin.common.error')));
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <RefreshCw size={20} color="#7c3aed" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <h2 style={{ marginBottom: '12px' }}>{t('admin.statuses.title')}</h2>
      <form onSubmit={handleSubmit} style={{ marginBottom: '16px', background: 'white', padding: '12px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <input type="number" placeholder={t('admin.statuses.value')} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
          <input placeholder={t('admin.statuses.code')} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <input placeholder={t('admin.statuses.labelTr')} value={form.labelTr} onChange={(e) => setForm({ ...form, labelTr: e.target.value })} />
          <input placeholder={t('admin.statuses.labelEn')} value={form.labelEn} onChange={(e) => setForm({ ...form, labelEn: e.target.value })} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ marginBottom: 0 }}>{t('admin.statuses.color')}:</label>
            <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
            <span style={{ fontSize: '12px' }}>{form.color}</span>
          </div>
        </div>
        {error && <div style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>{error}</div>}
        <button type="submit" disabled={saving} style={{ marginTop: '12px', padding: '8px 14px', background: '#7c3aed', color: 'white', borderRadius: '6px' }}>
          {editing ? t('admin.statuses.update') : t('admin.statuses.add')}
        </button>
        {editing && (
          <button type="button" onClick={resetForm} style={{ marginLeft: '10px', padding: '8px 14px' }}>
            {t('admin.statuses.cancel')}
          </button>
        )}
      </form>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
        {statuses.map(status => (
          <div key={status.id} style={{ padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>{status.value} - {status.code}</strong>
              <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: status.color, display: 'inline-block' }} />
            </div>
            <div style={{ fontSize: '13px', marginTop: '4px' }}>{status.labelTr} / {status.labelEn}</div>
            <div style={{ marginTop: '8px', display: 'flex', gap: '6px' }}>
              <button onClick={() => handleEdit(status)} style={{ padding: '5px', background: '#f3f4f6', borderRadius: '6px' }}><Edit size={14} /></button>
              <button onClick={() => handleDelete(status.id)} style={{ padding: '5px', background: '#fee2e2', borderRadius: '6px' }}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatusManagement;
